import { useCallback, useEffect, useRef, useState, type UIEvent } from "react";
import { fetchRequestTaskListPage, TaskListItem } from "../../../api/requests";
import { fetchItCheckTaskListPage } from "../../../api/itCheck";
import { acquireBodyScrollLock } from "../../../utils/lockBodyScroll";
import type { CheckDetailKind } from "../checkDetailKind";
import { TASK_LIST_MODAL_PAGE_SIZE } from "./requestDetailConstants";
import { mergeTaskListItems, sortTaskListByDirection } from "./requestDetailTaskListUtils";

interface UseRequestTaskListSidebarOptions {
  requestId: string;
  kind?: CheckDetailKind;
  taskList: TaskListItem[] | null;
  selectedTaskIndex: number;
  setSelectedTaskIndex: React.Dispatch<React.SetStateAction<number>>;
  ensureTaskLoadedInMainList: (taskId: string) => Promise<number>;
  closeActionsMenu: () => void;
}

export const useRequestTaskListSidebar = ({
  requestId,
  kind = "site",
  taskList,
  selectedTaskIndex,
  setSelectedTaskIndex,
  ensureTaskLoadedInMainList,
  closeActionsMenu,
}: UseRequestTaskListSidebarOptions) => {
  const [isTaskListModalOpen, setIsTaskListModalOpen] = useState(false);
  const [taskListModalItems, setTaskListModalItems] = useState<TaskListItem[]>([]);
  const [taskListModalPage, setTaskListModalPage] = useState(1);
  const [taskListModalTotalPages, setTaskListModalTotalPages] = useState(1);
  const [taskListModalLoading, setTaskListModalLoading] = useState(false);
  const [taskListSortDirection, setTaskListSortDirection] = useState<"asc" | "desc">("desc");
  const [isChecksModalOpen, setIsChecksModalOpen] = useState(false);
  const requestInFlightRef = useRef(false);

  const loadTaskListModalPage = useCallback(
    async ({ page, reset }: { page: number; reset?: boolean }) => {
      if (!requestId || requestInFlightRef.current) return;
      requestInFlightRef.current = true;
      setTaskListModalLoading(true);
      try {
        const response =
          kind === "it"
            ? await fetchItCheckTaskListPage(requestId, page, TASK_LIST_MODAL_PAGE_SIZE)
            : await fetchRequestTaskListPage(
                requestId,
                page,
                TASK_LIST_MODAL_PAGE_SIZE,
                taskListSortDirection
              );
        const sorted = sortTaskListByDirection(response.items, taskListSortDirection);
        setTaskListModalPage(response.page);
        setTaskListModalTotalPages(response.totalPages);
        setTaskListModalItems((prev) => (reset ? sorted : mergeTaskListItems(prev, sorted)));
      } catch {
        if (reset) setTaskListModalItems([]);
      } finally {
        requestInFlightRef.current = false;
        setTaskListModalLoading(false);
      }
    },
    [kind, requestId, taskListSortDirection]
  );

  const loadTaskListModalPageRef = useRef(loadTaskListModalPage);
  loadTaskListModalPageRef.current = loadTaskListModalPage;

  useEffect(() => {
    if (!isTaskListModalOpen) return;
    loadTaskListModalPageRef.current({ page: 1, reset: true });
  }, [requestId, isTaskListModalOpen, taskListSortDirection]);

  useEffect(() => {
    if (!isTaskListModalOpen) return;
    return acquireBodyScrollLock();
  }, [isTaskListModalOpen]);

  const handleOpenTaskListModal = useCallback(() => {
    closeActionsMenu();
    setIsTaskListModalOpen(true);
  }, [closeActionsMenu]);

  const handleCloseTaskListModal = useCallback(() => {
    setIsTaskListModalOpen(false);
  }, []);

  const handleOpenChecksModal = useCallback(() => {
    closeActionsMenu();
    setIsChecksModalOpen(true);
  }, [closeActionsMenu]);

  const handleCloseChecksModal = useCallback(() => {
    setIsChecksModalOpen(false);
  }, []);

  const closeAuxiliaryPanels = useCallback(() => {
    setIsTaskListModalOpen(false);
    setIsChecksModalOpen(false);
  }, []);

  const handleTaskListModalScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 64;
      const hasMorePages = taskListModalPage < taskListModalTotalPages;
      if (!nearBottom || !hasMorePages || taskListModalLoading) return;
      loadTaskListModalPage({ page: taskListModalPage + 1 });
    },
    [loadTaskListModalPage, taskListModalLoading, taskListModalPage, taskListModalTotalPages]
  );

  const handleSelectTaskFromModal = useCallback(
    async (taskId: string) => {
      let idx = taskList?.findIndex((task) => task.id === taskId) ?? -1;
      if (idx < 0) {
        idx = await ensureTaskLoadedInMainList(taskId);
      }
      if (idx >= 0) {
        setSelectedTaskIndex(idx);
      }
      setIsTaskListModalOpen(false);
    },
    [ensureTaskLoadedInMainList, setSelectedTaskIndex, taskList]
  );

  return {
    isTaskListModalOpen,
    taskListModalItems,
    taskListModalPage,
    taskListModalTotalPages,
    taskListModalLoading,
    taskListSortDirection,
    isChecksModalOpen,
    setTaskListSortDirection,
    handleOpenTaskListModal,
    handleCloseTaskListModal,
    handleOpenChecksModal,
    handleCloseChecksModal,
    handleTaskListModalScroll,
    handleSelectTaskFromModal,
    closeAuxiliaryPanels,
  };
};
